import type { BillingStanding } from '@/api/generated/enums';

import type { Company } from './api';

/*
 * The companies list as a due list (docs/Platform_Billing_Design.md §6): most urgent first, and two
 * filters for the questions it is opened to answer — who is about to owe, and who already does.
 * The standing is the server's; nothing here decides whether a company is late, only which late
 * company to look at first.
 */

/**
 * Lower is more urgent. A company that cannot save is first — somebody there is being refused right
 * now — then the late ones still working, then the ones about to owe, then the unfinished setup that
 * will never fall due on its own, then everything that needs nothing today.
 */
export const urgency: Record<BillingStanding, number> = {
  ReadOnly: 0,
  Overdue: 1,
  DueSoon: 2,
  NoTerms: 3,
  NoSubscription: 4,
  Suspended: 5,
  Pilot: 6,
  Leaving: 7,
  Paid: 8,
};

/** Most urgent first; within a standing, the one that ran out (or runs out) soonest; then by name. */
export function byUrgency(a: Company, b: Company): number {
  return (
    urgency[a.standing] - urgency[b.standing] ||
    (a.paidUntil ?? '9999').localeCompare(b.paidUntil ?? '9999') ||
    a.tenantName.localeCompare(b.tenantName)
  );
}

export type DueFilter = 'all' | 'dueSoon' | 'overdue';

/** Overdue counts the read-only as well as those in grace — both owe, as the dues tile counts them. */
const overdueStandings: readonly BillingStanding[] = ['Overdue', 'ReadOnly'];

export function matchesFilter(company: Company, filter: DueFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'dueSoon':
      return company.standing === 'DueSoon';
    case 'overdue':
      return overdueStandings.includes(company.standing);
  }
}

export const filterLabels: Record<DueFilter, string> = {
  all: 'All',
  dueSoon: 'Due in 30 days',
  overdue: 'Overdue',
};

/** How many each filter would show — for the label, so a filter with nothing in it says so. */
export function filterCounts(companies: readonly Company[]): Record<DueFilter, number> {
  return {
    all: companies.length,
    dueSoon: companies.filter((c) => matchesFilter(c, 'dueSoon')).length,
    overdue: companies.filter((c) => matchesFilter(c, 'overdue')).length,
  };
}

/** The list as shown: filtered, then most urgent first. */
export function dueList(companies: readonly Company[], filter: DueFilter): Company[] {
  return companies.filter((c) => matchesFilter(c, filter)).sort(byUrgency);
}
